import React from 'react'
import { Popover, List, ListItem, ListItemText } from '@mui/material'

export interface UserMenuItem {
    label: string
    href: string
}

interface UserMenuProps {
    open: boolean
    anchorEl: HTMLElement | null
    menuItems: UserMenuItem[]
    onClose: () => void
    onSignOut: () => void
    t: (key: string) => string
    variant: 'flyin' | 'cleartrip' | 'mobile'
    isRTL?: boolean
}

const UserMenu: React.FC<UserMenuProps> = ({ open, anchorEl, menuItems, onClose, onSignOut, t, variant, isRTL = false }) => {
    const mobileHorizontal = isRTL ? 'left' : 'right'
    
    const anchorOrigin = variant === 'mobile'
        ? { vertical: 'bottom' as const, horizontal: mobileHorizontal as 'left' | 'right' }
        : variant === 'flyin'
        ? { vertical: 'bottom' as const, horizontal: 'right' as const }
        : { vertical: 'bottom' as const, horizontal: 'right' as const }

    const transformOrigin = variant === 'mobile'
        ? { vertical: 'top' as const, horizontal: mobileHorizontal as 'left' | 'right' }
        : { vertical: 'top' as const, horizontal: 'right' as const }

    const paperProps = {
        sx: {
            mt: 1,
            borderRadius: 1.5,
            minWidth: 185,
            boxShadow: 3,
            p: 0,
            overflow: 'hidden',
            bgcolor: 'background.paper'
        }
    }

    const listItemSx = {
        display: 'flex',
        alignItems: 'center',
        gap: 0.8,
        py: 0.75,
        px: 2,
        minHeight: 40,
        border: 0,
        borderRadius: 0,
        fontWeight: 500,
        fontSize: 15,
        color: 'text.primary',
        bgcolor: 'background.paper',
        textDecoration: 'none',
        transition: 'background 0.2s',
        '&:hover': {
            bgcolor: 'action.hover',
            textDecoration: 'none'
        }
    }

    const signOutSx = {
        display: 'flex',
        alignItems: 'center',
        gap: 0.8,
        py: 0.5,
        px: 2,
        minHeight: 25,
        border: 0,
        borderRadius: 0,
        fontWeight: 500,
        fontSize: 15,
        color: 'error.main',
        bgcolor: 'background.paper',
        cursor: 'pointer',
        transition: 'background 0.2s',
        '&:hover': {
            bgcolor: 'action.hover'
        }
    }

    const itemTypographyProps = { fontSize: 15, fontWeight: 500, color: 'text.secondary' }
    const signOutTypographyProps = { fontSize: 15, fontWeight: 500, color: 'error.main' }

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
            PaperProps={paperProps}
        >
            <List sx={{ p: 0, maxHeight: 300, overflowY: 'auto' }}>
                {variant !== 'mobile' && menuItems.map((item, index) => (
                    <React.Fragment key={item.label}>
                        <ListItem
                            component="a"
                            href={item.href}
                            sx={listItemSx}
                        >
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={itemTypographyProps}
                            />
                        </ListItem>
                    </React.Fragment>
                ))}
                <ListItem
                    component="button"
                    onClick={onSignOut}
                    sx={signOutSx}
                >
                    <ListItemText
                        primary={t('userMenu.signOut')}
                        primaryTypographyProps={signOutTypographyProps}
                    />
                </ListItem>
            </List>
        </Popover>
    )
}

export { UserMenu }
export type { UserMenuProps }
